import { works } from "@/mocks/works";
import { newsList } from "@/mocks/news";
import type { Work, WorkType } from "@/types/media";

export function getWorkById(id: string | undefined): Work | undefined {
  if (!id) return undefined;
  return works.find((w) => w.id === id);
}

export function getWorksByType(type: WorkType): Work[] {
  return works.filter((w) => w.type === type);
}

export function getAllWorks(): Work[] {
  return works;
}

export function getRelatedWorks(current: Work, limit = 4): Work[] {
  const scored = works
    .filter((w) => w.id !== current.id)
    .map((w) => {
      let score = 0;
      if (w.director === current.director) score += 5;
      score += w.genres.filter((g) => current.genres.includes(g)).length * 2;
      if (w.type === current.type) score += 1;
      return { w, score };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.w);
}

export function getNewsById(id: string | undefined) {
  if (!id) return undefined;
  return newsList.find((n) => n.id === id);
}

export function getNewsByWorkId(workId: string) {
  return newsList.filter((n) => n.relatedWorkId === workId);
}

export interface WorkFilter {
  regions?: string[];
  years?: number[];
  genres?: string[];
  minRating?: number;
  sort?: "hot" | "year" | "rating";
}

export function filterWorks(type: WorkType, filter: WorkFilter): Work[] {
  let list = works.filter((w) => w.type === type);
  if (filter.regions && filter.regions.length) {
    list = list.filter((w) => filter.regions!.includes(w.region));
  }
  if (filter.years && filter.years.length) {
    list = list.filter((w) => filter.years!.includes(w.year));
  }
  if (filter.genres && filter.genres.length) {
    list = list.filter((w) => w.genres.some((g) => filter.genres!.includes(g)));
  }
  if (filter.minRating) {
    list = list.filter((w) => w.rating >= filter.minRating!);
  }
  const sort = filter.sort || "hot";
  list = [...list].sort((a, b) => {
    if (sort === "year") return b.year - a.year;
    if (sort === "rating") return b.rating - a.rating;
    return b.rating * 10 + b.year / 100 - (a.rating * 10 + a.year / 100);
  });
  return list;
}

export function searchWorks(keyword: string): Work[] {
  const k = keyword.trim().toLowerCase();
  if (!k) return [];
  return works.filter(
    (w) =>
      w.title.toLowerCase().includes(k) ||
      w.director.toLowerCase().includes(k) ||
      w.cast.some((c) => c.toLowerCase().includes(k)) ||
      w.genres.some((g) => g.toLowerCase().includes(k)) ||
      w.synopsis.toLowerCase().includes(k),
  );
}

export function searchNews(keyword: string) {
  const k = keyword.trim().toLowerCase();
  if (!k) return [];
  return newsList.filter(
    (n) =>
      n.title.toLowerCase().includes(k) ||
      n.summary.toLowerCase().includes(k) ||
      n.category.toLowerCase().includes(k),
  );
}

export function getFeaturedWorks(limit = 6): Work[] {
  return [...works].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export function getLatestNews(limit = 3) {
  return [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, limit);
}

export function getCarouselWorks(limit = 5): Work[] {
  return [...works].sort((a, b) => b.year - a.year).slice(0, limit);
}